import { useEffect, useRef, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import type {
  AnalyticsDateRange,
  AnalyticsFilters,
} from "@/features/analytics/types";
import { createDeviceListRequest } from "../utils";
import type { DeviceAggregateRow, DeviceAggregatesResponse } from "../types";

type UseDeviceAggregatesParams = {
  deviceIds: string[];
  selectedRange: AnalyticsDateRange;
  appliedFilters: AnalyticsFilters;
};

type UseDeviceAggregatesResult = {
  aggregates: Record<string, DeviceAggregateRow>;
  isLoading: boolean;
};

export const useDeviceAggregates = ({
  deviceIds,
  selectedRange,
  appliedFilters,
}: UseDeviceAggregatesParams): UseDeviceAggregatesResult => {
  const { performRequest } = useApp();
  const [aggregates, setAggregates] = useState<
    Record<string, DeviceAggregateRow>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const cacheKey = deviceIds.length > 0 ? deviceIds.join(",") : "";

  useEffect(() => {
    setAggregates({});
    if (deviceIds.length === 0) {
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    const base = createDeviceListRequest(
      selectedRange,
      appliedFilters,
      1,
      deviceIds.length,
    );

    const payload = {
      deviceIds,
      filters: base.filters,
      createdAt: base.createdAt,
    };

    setIsLoading(true);
    performRequest<DeviceAggregatesResponse | null>("/devices/aggregates", {
      method: "POST",
      body: JSON.stringify(payload),
      signal: controller.signal,
    })
      .then((response) => {
        if (controller.signal.aborted) return;
        const next: Record<string, DeviceAggregateRow> = {};
        if (response?.rows) {
          for (const row of response.rows) next[row.deviceId] = row;
        }
        setAggregates(next);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        // eslint-disable-next-line no-console
        console.error("Failed to load device aggregates", error);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, selectedRange, appliedFilters, performRequest]);

  return { aggregates, isLoading };
};
